
import React from 'react';
import {Box} from '@mui/material';
import './Court.css';

import { ServeOutLocation } from "sandy-shared/dist/types/servce-info.type";
import { AttackOutLocation } from "sandy-shared/dist/types/attack-info.type";


export interface SectionAdditionalClasses {
  receivingOutOfBounds?: string;
  receivingInBounds?: string;
  attackLocationOrFree?: string;
  shotLocation?: string;
  attackingOutOfBounds?: string;
}

export interface LocationDisplaySettings {
  showAttackLocations: boolean;
  showServeReceiveLocation: boolean;
}

export interface LocationConditionalStyles {
  attackDestinationSection?: (rowIndex: number, colIndex: number) => React.CSSProperties;
  attackOriginSection?: (attackLocationIndex: number) => React.CSSProperties;
  freeBallLocationSection?: () => React.CSSProperties;
  serveReceiveSection?: (locationIndex: number) => React.CSSProperties;
  attackOutOfBoundsLocation?: (location: AttackOutLocation) => React.CSSProperties;
  serveOutOfBoundLocation?: (location: ServeOutLocation) => React.CSSProperties;
}

export interface ClickHandlers {
  handleServeReceiveSectionClick?: (locationIndex: number) => void;
  handleAttackOriginClick?: (locationIndex: number) => void;
  handleAttackDestinationSectionClick?: (rowIndex: number, colIndex: number) => void;
  handleAttackOutClick?: (outLocation: AttackOutLocation) => void;
  handleFreeBallClick?: () => void;
  handleOutOfBoundsServeClick?: (serveOutLocation: ServeOutLocation) => void;
}

export interface ConditionalContent {
  serveReceiveLocation?: (locationIndex: number) => React.ReactNode;
  attackOrigin?: (attackLocationIndex: number) => React.ReactNode;
  attackDestination?: (rowIndex: number, colIndex: number) => React.ReactNode;
  attackOutOfBounds?: (outLocation: AttackOutLocation) => React.ReactNode;
}

export interface CourtProps {
  isRotated?: boolean;
  sectionAdditionalClasses?: SectionAdditionalClasses;
  locationDisplaySettings?: LocationDisplaySettings;
  locationConditionalStyles?: LocationConditionalStyles;
  clickHandlers?: ClickHandlers;
  conditionalContent?: ConditionalContent;
}

const Court = (props: CourtProps) => {
  const courtIsRotated = props.isRotated ?? false;

  const receivingOutOfBoundsAdditionalClasses = props.sectionAdditionalClasses?.receivingOutOfBounds ?? '';
  const receivingInBoundsAdditionalClasses = props.sectionAdditionalClasses?.receivingInBounds ?? '';
  const attackLocationOrFreeAdditionalClasses = props.sectionAdditionalClasses?.attackLocationOrFree ?? '';
  const shotLocationAdditionalClasses = props.sectionAdditionalClasses?.shotLocation ?? '';
  const attackingOutOfBoundsAdditionalClasses = props.sectionAdditionalClasses?.attackingOutOfBounds ?? '';

  const attackDestinationSectionStyle = props.locationConditionalStyles?.attackDestinationSection ?? (() => ({}));
  const attackOriginSectionStyle = props.locationConditionalStyles?.attackOriginSection ?? (() => ({}));
  const freeBallLocationSectionStyle = props.locationConditionalStyles?.freeBallLocationSection ?? (() => ({}));
  const serveReceiveSectionStyle = props.locationConditionalStyles?.serveReceiveSection ?? (() => ({}));
  const attackOutOfBoundsLocationStyle = props.locationConditionalStyles?.attackOutOfBoundsLocation ?? (() => ({}));
  const serveOutOfBoundsLocationStyle = props.locationConditionalStyles?.serveOutOfBoundLocation ?? (() => ({}));

  const handleServeReceiveSectionClick = props.clickHandlers?.handleServeReceiveSectionClick ?? (() => {});
  const handleAttackOriginClick = props.clickHandlers?.handleAttackOriginClick ?? (() => {});
  const handleShotLocationSectionClick = props.clickHandlers?.handleAttackDestinationSectionClick ?? (() => {});
  const handleAttackOutError = props.clickHandlers?.handleAttackOutClick ?? (() => {});
  const handleFreeBallClick = props.clickHandlers?.handleFreeBallClick ?? (() => {});
  const handleOutOfBoundsServe = props.clickHandlers?.handleOutOfBoundsServeClick ?? (() => {});

  const serveReceiveLocationContent = props.conditionalContent?.serveReceiveLocation ?? (() => null);
  const attackOriginContent = props.conditionalContent?.attackOrigin ?? (() => null);
  const attackDestinationContent = props.conditionalContent?.attackDestination ?? (() => null);
  const attackOutOfBoundsContent = props.conditionalContent?.attackOutOfBounds ?? (() => null);

  return (
    <Box className={`court-and-out-of-bounds ${courtIsRotated ? 'rotate-180' : ''}`}>
      <Box className={'long-out-of-bounds'} style={{borderTop: '2px solid black'}}>
        <Box
          className={`long-out-of-bounds-subsection ${attackingOutOfBoundsAdditionalClasses}`}
          onClick={() => handleAttackOutError(AttackOutLocation.DeepLeft)}
          style={{...attackOutOfBoundsLocationStyle(AttackOutLocation.DeepLeft), borderLeft: '2px solid black', borderRight: '1px solid black'}}>
          {attackOutOfBoundsContent(AttackOutLocation.DeepLeft)}
        </Box>
        <Box
          className={`long-out-of-bounds-subsection ${attackingOutOfBoundsAdditionalClasses}`}
          onClick={() => handleAttackOutError(AttackOutLocation.DeepRight)}
          style={{...attackOutOfBoundsLocationStyle(AttackOutLocation.DeepRight), borderLeft: '1px solid black', borderRight: '2px solid black'}}>
          {attackOutOfBoundsContent(AttackOutLocation.DeepRight)}
        </Box>
      </Box>
      <Box className={'court-and-wide-out-of-bounds'}>
        <Box className={'wide-out-of-bounds'}> {/*Left out of bounds*/}
          <Box className={`side-wide-out-of-bounds`} style={{borderBottom: '2px solid black'}}>
            <Box
              className={`side-wide-out-of-bounds-subsection ${attackingOutOfBoundsAdditionalClasses}`}
              onClick={() => handleAttackOutError(AttackOutLocation.WideLeftDeep)}
              style={{...attackOutOfBoundsLocationStyle(AttackOutLocation.WideLeftDeep), borderBottom: '1px solid black'}}>
              {attackOutOfBoundsContent(AttackOutLocation.WideLeftDeep)}
            </Box>
            <Box
              className={`side-wide-out-of-bounds-subsection ${attackingOutOfBoundsAdditionalClasses}`}
              onClick={() => handleAttackOutError(AttackOutLocation.WideLeftShallow)}
              style={{...attackOutOfBoundsLocationStyle(AttackOutLocation.WideLeftShallow), borderTop: '1px solid black'}}>
              {attackOutOfBoundsContent(AttackOutLocation.WideLeftShallow)}
            </Box>
          </Box>
          <Box className={'side-wide-out-of-bounds'} >
            <Box
              className={`side-wide-out-of-bounds-subsection ${receivingOutOfBoundsAdditionalClasses}`}
              onClick={() => handleOutOfBoundsServe(ServeOutLocation.WideRightShallow)}
              style={{...serveOutOfBoundsLocationStyle(ServeOutLocation.WideRightShallow), borderTop: '2px solid black', borderBottom: '1px solid black'}}>
            </Box>
            <Box
              className={`side-wide-out-of-bounds-subsection ${receivingOutOfBoundsAdditionalClasses}`}
              onClick={() => handleOutOfBoundsServe(ServeOutLocation.WideRightDeep)}
              style={{...serveOutOfBoundsLocationStyle(ServeOutLocation.WideRightDeep), borderTop: '1px solid black'}}>
            </Box>
          </Box>
        </Box>
        <Box className={'in-bounds-court'}>
          <Box className={'shot-location-grid'}>
            {[...Array(3)].map((_, rowIndex) => (
              [...Array(5)].map((_, colIndex) => (
                <Box
                  key={`${rowIndex}-${colIndex}`}
                  className={`shot-location-section ${shotLocationAdditionalClasses}`}
                  style={attackDestinationSectionStyle(rowIndex, colIndex)}
                  onClick={() => handleShotLocationSectionClick(rowIndex, colIndex)}>
                  {attackDestinationContent(rowIndex, colIndex)}
                </Box>
              ))
            ))}
          </Box>
          {props.locationDisplaySettings?.showAttackLocations && <Box className={'attack-locations-and-free'}>
            <Box className={'on-and-off-attack-locations'}>
              {[...Array(5)].map((_, index) => (
                <Box className={'on-and-off-attack-location'} key={`on-and-off-attack-location-${index}`}>
                  <Box
                    className={`on-attack-location ${attackLocationOrFreeAdditionalClasses}`}
                    style={attackOriginSectionStyle(index)}
                    onClick={() => handleAttackOriginClick(index)}>
                    {attackOriginContent(index)}
                  </Box>
                </Box>))}
            </Box>
            <Box
              className={`attack-free-ball ${attackLocationOrFreeAdditionalClasses}`}
              style={freeBallLocationSectionStyle()}
              onClick={() => handleFreeBallClick()}>
            </Box>
          </Box>}
          {props.locationDisplaySettings?.showServeReceiveLocation && 
            <div style={{height: '100%', width: '100%'}}>
              <Box className={'serve-receive-grid'}>
                {
                  [...Array(4)].map((_, colIndex) => {
                    const indicesToSkip = colIndex === 0 ? 0 : colIndex === 3 ? 2 : 1; // This accounts for the directly at serve receive locations (which go between index 0 and 1 and between index2 and 3)
                    const locationIndex = colIndex + indicesToSkip;
                    return <Box
                      key={`${colIndex}`}
                      className={`serve-receive-lane ${receivingInBoundsAdditionalClasses}`}
                      style={serveReceiveSectionStyle(locationIndex)}
                      onClick={() => handleServeReceiveSectionClick(locationIndex)}
                    >
                      {serveReceiveLocationContent(locationIndex)}
                    </Box>}
                  )
                }
              </Box>
              <Box style={{position: 'relative', top: '-50%', left: '0', zIndex: 10, height: '50%', width: '100%', display: 'flex', flexDirection: 'row', pointerEvents: 'none'}}>
                <Box style={{height: '100%', width: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <Box 
                    className={receivingInBoundsAdditionalClasses}
                    style={{height: '27%', width: '50%', border: '2px solid black', boxSizing: 'border-box', pointerEvents: 'all', ...serveReceiveSectionStyle(1)}}
                    onClick={() => handleServeReceiveSectionClick(1)}>
                    {serveReceiveLocationContent(1)}
                  </Box>
                </Box>
                <Box style={{height: '100%', width: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <Box 
                    className={receivingInBoundsAdditionalClasses}
                    style={{height: '27%', width: '50%', border: '2px solid black', boxSizing: 'border-box', pointerEvents: 'all', ...serveReceiveSectionStyle(4)}}
                    onClick={() => handleServeReceiveSectionClick(4)}
                  >
                    {serveReceiveLocationContent(4)}
                  </Box>
                </Box>
              </Box>
            </div>
          }
        </Box>
        <Box className={'wide-out-of-bounds'}> {/*Right out of bounds*/}
          <Box className={'side-wide-out-of-bounds'}  style={{borderBottom: '2px solid black'}}>
            <Box
              className={`side-wide-out-of-bounds-subsection ${attackingOutOfBoundsAdditionalClasses}`}
              onClick={() => handleAttackOutError(AttackOutLocation.WideRightDeep)}
              style={{...attackOutOfBoundsLocationStyle(AttackOutLocation.WideRightDeep), borderBottom: '1px solid black'}}>
              {attackOutOfBoundsContent(AttackOutLocation.WideRightDeep)}
            </Box>
            <Box
              className={`side-wide-out-of-bounds-subsection ${attackingOutOfBoundsAdditionalClasses}`}
              onClick={() => handleAttackOutError(AttackOutLocation.WideRightShallow)}
              style={{...attackOutOfBoundsLocationStyle(AttackOutLocation.WideRightShallow), borderTop: '1px solid black'}}>
              {attackOutOfBoundsContent(AttackOutLocation.WideRightShallow)}
            </Box>
          </Box>
          <Box className={'side-wide-out-of-bounds'}>
            <Box
              className={`side-wide-out-of-bounds-subsection ${receivingOutOfBoundsAdditionalClasses}`}
              onClick={() => handleOutOfBoundsServe(ServeOutLocation.WideLeftShallow)}
              style={{...serveOutOfBoundsLocationStyle(ServeOutLocation.WideLeftShallow), borderTop: '2px solid black', borderBottom: '1px solid black'}}>
            </Box>
            <Box
              className={`side-wide-out-of-bounds-subsection ${receivingOutOfBoundsAdditionalClasses}`}
              onClick={() => handleOutOfBoundsServe(ServeOutLocation.WideLeftDeep)}
              style={{...serveOutOfBoundsLocationStyle(ServeOutLocation.WideLeftDeep), borderTop: '1px solid black'}}>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box className={'long-out-of-bounds'} style={{borderBottom: '2px solid black'}}>
        <Box
          className={`long-out-of-bounds-subsection ${receivingOutOfBoundsAdditionalClasses}`}
          onClick={() => handleOutOfBoundsServe(ServeOutLocation.DeepRight)}
          style={{...serveOutOfBoundsLocationStyle(ServeOutLocation.DeepRight), borderLeft: '2px solid black', borderRight: '1px solid black'}}>
        </Box>
        <Box
          className={`long-out-of-bounds-subsection ${receivingOutOfBoundsAdditionalClasses}`}
          onClick={() => handleOutOfBoundsServe(ServeOutLocation.DeepLeft)}
          style={{...serveOutOfBoundsLocationStyle(ServeOutLocation.DeepLeft), borderLeft: '1px solid black', borderRight: '2px solid black'}}>
        </Box>
      </Box>
    </Box>
  );
};

export default Court;
